import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { CTABand } from "../components/CTABand";
import { PROCESS_STEPS } from "../data/content";

const StepRow = ({ step, i }) => (
  <div className="relative pb-16 last:pb-0 md:pb-24">
    <div className="grid items-center gap-8 md:grid-cols-2 md:gap-20">
      <Reveal className={i % 2 === 1 ? "md:order-2" : ""}>
        <img
          src={step.img}
          alt={`${step.name} — Paramount Kitchen manufacturing process step ${step.n}`}
          loading="lazy"
          className="aspect-[4/3] w-full rounded-3xl object-cover shadow-lg"
        />
      </Reveal>
      <Reveal delay={0.1} className={i % 2 === 1 ? "md:order-1" : ""}>
        <div>
          <span className="font-display text-6xl font-semibold text-gold/40 md:text-7xl">
            {step.n}
          </span>
          <h2 className="mt-4 font-display text-3xl font-medium text-ink md:text-4xl">
            {step.name}
          </h2>
          <p className="mt-4 max-w-md leading-relaxed text-ink/65">{step.text}</p>
        </div>
      </Reveal>
    </div>
  </div>
);

export default function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.75", "end 0.6"] });
  const h = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <>
      <SEO
        title="Our Manufacturing Process — 10 Engineered Steps | Paramount Kitchen"
        description="From material selection to installation: the ten-step Paramount Kitchen manufacturing process — lamination, 50-ton hydraulic cold pressing, T3200-M precision cutting, edge banding, assembly and inspection."
      />

      <section className="bg-royal pb-20 pt-36 text-ivory md:pb-28 md:pt-44">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow text-gold">Our Process</p>
            <h1 className="text-balance mt-5 max-w-3xl font-display text-4xl font-medium leading-tight md:text-6xl">
              Ten steps. <span className="serif-accent text-gold">Zero shortcuts.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-ivory/65">
              Every Paramount Kitchen moves through the same disciplined manufacturing sequence —
              from carefully selected material to finished installation.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-28">
        <div ref={ref} className="relative mx-auto max-w-7xl px-6">
          <div className="absolute left-6 top-0 hidden h-full w-px bg-ink/10 md:left-1/2 md:block" >
            <motion.div style={{ height: h }} className="w-full bg-gold" />
          </div>
          <div data-testid="process-timeline" className="md:space-y-4">
            {PROCESS_STEPS.map((s, i) => (
              <div key={s.n} className="relative md:pl-0">
                <span className="absolute left-6 top-2 hidden h-3 w-3 -translate-x-1/2 rounded-full border-2 border-gold bg-ivory md:left-1/2 md:block" />
                <StepRow step={s} i={i} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
